import { memo, useState, useEffect, useRef } from 'react';
import { useStore } from 'store/StoreContext';
import { clearGenerator } from 'store/login-user/LoginUserData';
import { Link, useHistory } from 'react-router-dom';
import { useUpdatePassword } from 'hooks/UseUpdatePassword';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/PasswordValidator';
import { CompleteDialog } from 'components/organisms/CompleteDialog';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress } from '@material-ui/core';
import { PartialdAnnotation } from 'components/molecules/partial-annotation/PartialAnnotation';
import {
	MESSAGE_PASSWORD_ANNOTATION,
	POPOVER_MESSAGE_PASSWORD_ANNOTATION,
	TARGET_MESSAGE_PASSWORD_POPOVER,
} from 'utils/messages';

import classes from './override.module.scss';

type Props = {};

export type Passwords = {
	oldPassword: string;
	newPassword: string;
	newPasswordConfirm: string;
};

export const PasswordEditTemplate = memo((_props: Props) => {
	const history = useHistory();
	const [store, dispatch] = useStore(); // eslint-disable-line
	const [formData, setFormData] = useState<Passwords>({ oldPassword: '', newPassword: '', newPasswordConfirm: '' });
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const handleDialogOpen = () => {
		setIsDialogOpen(true);
	};
	const handleDialogClose = () => {
		setIsDialogOpen(false);
		clearGenerator(dispatch);
		history.push(RoutePath.LOGIN);
	};
	const [processLoading, setProcessLoading] = useState(false);
	const { update } = useUpdatePassword(
		formErrors,
		setFormErrors,
		getErrorMessage,
		actionError,
		setActionError,
		formData,
		handleDialogOpen,
		setProcessLoading,
	);

	// パスワード注釈
	const splitText = new RegExp('(' + TARGET_MESSAGE_PASSWORD_POPOVER + ')');
	const annotationMessage = MESSAGE_PASSWORD_ANNOTATION.split(splitText);

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({ ...formData, [event.target.name]: event.target.value });
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDマイページ｜パスワード変更';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>パスワード変更</li>
					</ul>
				</div>

				<h1 className="page-head">パスワード変更</h1>

				<div className="section-in">
					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.oldPassword !== '' ? <p>【現在のパスワード】{formErrors.oldPassword}</p> : null}
										{formErrors.newPassword !== '' ? <p>【新しいパスワード】{formErrors.newPassword}</p> : null}
										{formErrors.newPasswordConfirm !== '' ? (
											<p>【新しいパスワードの確認】{formErrors.newPasswordConfirm}</p>
										) : null}
									</div>
								</>
							) : null}
						</div>
					) : null}
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">現在のパスワード</label>
						</div>
						<div className="form-input width50">
							<input type="password" name="oldPassword" value={formData.oldPassword} onChange={handleChangeValue} />
						</div>
						<p className="error">{formErrors.oldPassword}</p>
					</div>
					<div className="form-box">
						<div className={`form-head ${classes.displayFlex}`}>
							<div className={classes.newPasswordLabelBox}>
								<label className={classes.newPasswordLabel} htmlFor="">
									新しいパスワード
								</label>
							</div>
							<div>
								<span className="annotation">
									<PartialdAnnotation
										headerMessage={annotationMessage[0]}
										footerMessage={annotationMessage[2]}
										targetMessage={annotationMessage[1]}
										popoverMessage={POPOVER_MESSAGE_PASSWORD_ANNOTATION}
										isBreakBeforeTargetText={true}
									/>
								</span>
							</div>
						</div>
						<div className="form-input width50">
							<input type="password" name="newPassword" value={formData.newPassword} onChange={handleChangeValue} />
						</div>

						<p className="error">{formErrors.newPassword}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">新しいパスワードの確認</label>
						</div>
						<div className="form-input width50">
							<input
								type="password"
								name="newPasswordConfirm"
								value={formData.newPasswordConfirm}
								onChange={handleChangeValue}
							/>
						</div>
						<p className="error">{formErrors.newPasswordConfirm}</p>
					</div>

					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={update}>パスワードを変更する</button>}
						</div>
					</div>
				</div>
			</section>
			{/* 完了ダイアログ */}
			<CompleteDialog isOpen={isDialogOpen} doTrans={handleDialogClose} buttonLabel="ログイン画面" />
		</>
	);
});
